import { ChangeEvent, FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { faChevronDown, faChevronUp, faGear, faSearch } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { SearchField } from "@/components/SearchField/SearchField";
import { Spinner } from "@/components/Spinner/Spinner";

import { ListSourceType } from "@/enum/list.enum";

import { useGetLeanListsQuery } from "@/pages/Private/redux/list/list.api";

import { FeedSelectedList } from "@/pages/Private/redux/feed/feed.slice";

import { AnchoredPopover } from "@/pages/Private/pages/Contacts/components/LeadPanelActions/AnchoredPopover";

/** Chip label of the current list selection: the list name, or "N lists". */
export const getFeedListSelectionLabel = (
	selectedLists: FeedSelectedList[],
	t: (key: string, options?: Record<string, unknown>) => string
): string => {
	if (selectedLists.length === 0) {
		return t("feed.listSelection.none");
	}

	if (selectedLists.length === 1) {
		return selectedLists[0].title;
	}

	return t("feed.listSelection.multiple", { count: selectedLists.length });
};

const SEARCH_DEBOUNCE_MS = 400;
const LISTS_LIMIT = 100;
const PANEL_WIDTH = 320;

export interface FeedListSwitcherProps {
	selectedLists: FeedSelectedList[];
	onChange: (lists: FeedSelectedList[]) => void;
	/** Extension escape-hatch (web rule §11) — panels portal into `portalContainer ?? document.body`. */
	portalContainer?: HTMLElement | null;
}

/**
 * Tracker-list multi-select of the Feed topbar: a chip trigger ("list name" /
 * "N lists") opening a portaled panel with search + checkbox rows. The last
 * selected list cannot be deselected — the feed always has a selection.
 *
 * Each row carries a gear linking to that list's settings (PREC-3698) — a tracker is a list, so
 * that is the list settings step. It is the per-list route to the settings page, so a multi-select
 * — where the topbar cluster has no single target — can still reach any one of them.
 */
export const FeedListSwitcher: FunctionComponent<FeedListSwitcherProps> = ({
	selectedLists,
	onChange,
	portalContainer,
}) => {
	const { t } = useTranslation();

	const triggerRef = useRef<HTMLButtonElement>(null);
	const [isOpen, setIsOpen] = useState(false);

	const [searchValue, setSearchValue] = useState("");
	const [query, setQuery] = useState("");
	const searchTimerRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		if (searchTimerRef.current) {
			clearTimeout(searchTimerRef.current);
		}

		searchTimerRef.current = setTimeout(() => setQuery(searchValue), SEARCH_DEBOUNCE_MS);

		return () => {
			if (searchTimerRef.current) {
				clearTimeout(searchTimerRef.current);
			}
		};
	}, [searchValue]);

	const { data: trackerLists, isFetching } = useGetLeanListsQuery(
		{ page: 1, limit: LISTS_LIMIT, searchValue: query, sourceType: ListSourceType.TRACKER },
		{ skip: !isOpen }
	);

	const isSelected = (id: number) => selectedLists.some((list) => Number(list.id) === id);

	const handleToggle = (list: { id: number; name: string }) => {
		if (isSelected(list.id)) {
			// Never allow an empty selection — the feed needs at least one list.
			if (selectedLists.length === 1) {
				return;
			}

			onChange(selectedLists.filter((selected) => Number(selected.id) !== list.id));

			return;
		}

		onChange([...selectedLists, { id: list.id, title: list.name }]);
	};

	return (
		<>
			<button
				ref={triggerRef}
				className="flex items-center gap-1.5 h-[32px] px-3 text-xs font-semibold text-text-meta border border-gray-200 rounded-[10px] hover:border-brand-light duration-200 cursor-pointer max-w-[280px]"
				data-testid="feed-list-switcher-trigger"
				type="button"
				onClick={() => setIsOpen((open) => !open)}
			>
				<span className="truncate" data-testid="feed-list-selection-label">
					{getFeedListSelectionLabel(selectedLists, t)}
				</span>
				<Icon className="w-3 h-3 shrink-0" icon={isOpen ? faChevronUp : faChevronDown} />
			</button>

			{isOpen && (
				<AnchoredPopover
					anchorEl={triggerRef.current}
					portalContainer={portalContainer}
					width={PANEL_WIDTH}
					onClose={() => setIsOpen(false)}
				>
					<div
						className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
						data-testid="feed-list-switcher-panel"
					>
						<div className="p-2">
							<SearchField
								classNameInput="!h-[32px] !text-xs !pl-10"
								handleChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
								handleReset={() => setSearchValue("")}
								icon={<Icon className="h-[14px]" icon={faSearch} />}
								iconPosition="left"
								name="feed-list-switcher-search"
								placeholder={t("feed.listSelection.searchPlaceholder")}
								value={searchValue}
							/>
						</div>

						<div className="bb-scrollbar-darker max-h-[280px] overflow-y-auto">
							{isFetching ? (
								<div className="flex items-center justify-center py-4">
									<Spinner color="#4d66f5" />
								</div>
							) : (trackerLists?.data.length ?? 0) > 0 ? (
								trackerLists?.data.map((list) => (
									<div
										key={list.id}
										className="group flex items-center w-full text-sm text-gray-900 hover:bg-gray-100 duration-150"
									>
										<Checkbox
											className="pl-2 min-w-0 flex-1"
											isChecked={isSelected(list.id)}
											labelClass="w-full cursor-pointer py-2 px-3 pl-0"
											onChange={() => handleToggle(list)}
										>
											<span className="whitespace-nowrap overflow-hidden text-ellipsis">
												{list.name}
											</span>
										</Checkbox>

										<Link
											aria-label={t("feed.listSelection.openSettings", { name: list.name })}
											className="shrink-0 flex items-center justify-center w-8 h-8 mr-1 rounded-md text-text-meta hover:text-text hover:bg-gray-200 duration-150"
											data-testid={`feed-list-switcher-settings-${list.id}`}
											title={t("feed.listSelection.openSettings", { name: list.name })}
											to={`/app/lists/create/settings/${list.id}`}
											onClick={() => setIsOpen(false)}
										>
											<Icon className="w-3 h-3" icon={faGear} />
										</Link>
									</div>
								))
							) : (
								<p className="px-3 py-2 text-sm italic text-gray-500">{t("basics.noResults")}</p>
							)}
						</div>
					</div>
				</AnchoredPopover>
			)}
		</>
	);
};
